/**
 * @file Helpers for storing SAK-related data in local storage.
 */

import { wvusLogger } from "../observability/logging";

const SAK_PHOTO_KEY = "sakPhotoData";
const SAK_QUESTIONNAIRE_KEY = "sakQuestionnaireData";
const SAK_SPONSOR_KEY = "sakSponsorData";

export type SakPhotoData = {
  mediaUrl: string;
  mediaHandshake: string;
};

export type SakQuestionnaireData = {
  topic: string;
  response: string;
};

// same as billingData
export type SakSponsorData = {
  [key: string]: string | undefined;
};

/**
 * Stores SAK photo data in local storage.
 * @param data The photo data to store
 */
export const storeSakPhotoData = (data: SakPhotoData) => {
  try {
    localStorage.setItem(SAK_PHOTO_KEY, JSON.stringify(data));
  } catch (err) {
    const error = new Error(
      "Unable to save SAK photo data to localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Retrieves SAK photo data from local storage.
 * @returns The stored photo data if found, otherwise null
 */
export const retrieveSakPhotoData = (): SakPhotoData | null => {
  try {
    const data = localStorage.getItem(SAK_PHOTO_KEY);
    return data ? JSON.parse(data) : null;
  } catch (err) {
    const error = new Error(
      "Unable to parse SAK photo data from localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    return null;
  }
};

/**
 * Removes SAK photo data from local storage.
 */
export const removeSakPhotoData = () => {
  try {
    localStorage.removeItem(SAK_PHOTO_KEY);
  } catch (err) {
    const error = new Error(
      "Unable to remove SAK photo data from localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Stores SAK questionnaire data in local storage.
 * @param data The questionnaire data to store
 */
export const storeSakQuestionnaireData = (data: SakQuestionnaireData) => {
  try {
    localStorage.setItem(SAK_QUESTIONNAIRE_KEY, JSON.stringify(data));
  } catch (err) {
    const error = new Error(
      "Unable to save SAK questionnaire data to localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Retrieves SAK questionnaire data from local storage.
 * @returns The stored questionnaire data if found, otherwise null
 */
export const retrieveSakQuestionnaireData = (): SakQuestionnaireData | null => {
  try {
    const data = localStorage.getItem(SAK_QUESTIONNAIRE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (err) {
    const error = new Error(
      "Unable to parse SAK questionnaire data from localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    return null;
  }
};

/**
 * Removes SAK questionnaire data from local storage.
 */
export const removeSakQuestionnaireData = () => {
  try {
    localStorage.removeItem(SAK_QUESTIONNAIRE_KEY);
  } catch (err) {
    const error = new Error(
      "Unable to remove SAK questionnaire data from localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Stores SAK sponsor data in local storage.
 * @param data The sponsor data to store
 */
export const storeSakSponsorData = (data: SakSponsorData) => {
  try {
    localStorage.setItem(SAK_SPONSOR_KEY, JSON.stringify(data));
  } catch (err) {
    const error = new Error(
      "Unable to save SAK sponsor data to localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};

/**
 * Retrieves SAK sponsor data from local storage.
 * @returns The stored sponsor data if found, otherwise null
 */
export const retrieveSakSponsorData = (): SakSponsorData | null => {
  try {
    const data = localStorage.getItem(SAK_SPONSOR_KEY);
    return data ? JSON.parse(data) : null;
  } catch (err) {
    const error = new Error(
      "Unable to parse SAK sponsor data from localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
    return null;
  }
};

/**
 * Removes SAK sponsor data from local storage.
 */
export const removeSakSponsorData = () => {
  try {
    localStorage.removeItem(SAK_SPONSOR_KEY);
  } catch (err) {
    const error = new Error(
      "Unable to remove SAK sponsor data from localstorage because: " + err.message
    );
    wvusLogger.log(error, wvusLogger.getLogLevelNames().ERROR);
  }
};
